import type { CategoryTreeNode } from '@repo/api-contracts';
import { Plus } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { STORE_PATH } from '@/shared/config';
import { buttonVariants } from '@/shared/ui';
import { HomeCategoryColumn } from './home-category-column';

/** 한 줄에 세우는 대분류 수 — 데스크톱 세 칸. */
export const HOME_CATEGORY_GROUPS = 3;

interface HomeCategoriesProps {
  /** 스토어 카테고리 트리 — 앞에서부터 세 대분류만 쓴다(운영자가 정한 노출 순서). */
  readonly groups: readonly CategoryTreeNode[];
  /** 대분류 id → 대표 사진 주소(null 은 없음). Map 에 없으면 아직 불러오는 중. */
  readonly covers: ReadonlyMap<string, string | null>;
  readonly isPending?: boolean;
  readonly isError?: boolean;
  readonly onRetry?: () => void;
}

/**
 * HOME 카테고리 — 추천 상품과 같은 머리(작은 영문 머리말·굵은 제목·테두리 알약 링크) 아래
 * 대분류 세 칸을 나란히 세운다. 칸마다 대표 사진·대분류 이름·소분류 줄 목록이고 전부 스토어 목록 링크다.
 * 성공한 빈 트리는 섹션을 생략하고, 초기 로딩·실패는 별도로 안내한다.
 */
export const HomeCategories = ({
  groups,
  covers,
  isPending = false,
  isError = false,
  onRetry,
}: HomeCategoriesProps): ReactElement | null => {
  const visible = groups.slice(0, HOME_CATEGORY_GROUPS);
  if (visible.length === 0 && !isPending && !isError) return null;

  return (
    <section aria-labelledby="home-categories-heading" className="w-full px-5 sm:px-8 xl:px-14">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-3">
          <p className="text-base text-muted-foreground sm:text-lg">Category</p>
          <h2
            id="home-categories-heading"
            className="text-[2rem] leading-tight font-bold tracking-[-0.03em] sm:text-[2.75rem]"
          >
            원스톱으로 끝내는 맞춤형 굿즈 제작
          </h2>
        </div>
        <Link
          to={STORE_PATH}
          className="inline-flex h-12 items-center gap-2 self-start rounded-full border border-foreground px-6 text-[15px] font-medium transition-colors hover:bg-foreground hover:text-background sm:self-auto"
        >
          스토어 보기
          <Plus aria-hidden strokeWidth={1.75} className="size-4" />
        </Link>
      </div>

      {isPending && visible.length === 0 ? (
        <p role="status" className="mt-10 text-muted-foreground">
          카테고리를 불러오는 중입니다.
        </p>
      ) : null}
      {isError ? (
        <div
          role="alert"
          className="mt-10 flex flex-wrap items-center justify-between gap-4 border-y border-foreground/10 py-5"
        >
          <p className="text-sm text-destructive">카테고리를 불러오지 못했습니다.</p>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className={buttonVariants({ variant: 'outline', size: 'sm' })}
            >
              다시 시도
            </button>
          ) : null}
        </div>
      ) : null}

      {visible.length > 0 ? (
        <ul className="mt-10 grid gap-x-8 gap-y-14 sm:mt-12 md:grid-cols-2 lg:grid-cols-3 xl:gap-x-12">
          {visible.map((group) => (
            <li key={group.id} className="min-w-0">
              <HomeCategoryColumn group={group} coverUrl={covers.get(group.id)} />
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
};
